import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import { pickInitialValuesForFieldSelectTree } from '../../../util/search';

import { FieldSelectTree } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectSingleFilter.module.css';

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

const getBranchPath = (primaryOptions, currentSelections) => {
  const currentSelectionsEntries = Object.entries(currentSelections).sort(([k, v]) => k);
  return currentSelectionsEntries.reduce((branchPath, entry) => {
    const [levelKey, selectedOption] = entry;

    const currentOptions =
      branchPath.length > 0 ? branchPath[branchPath.length - 1].suboptions : primaryOptions;
    const foundOption = currentOptions.find(o => o.option === selectedOption);
    const foundOptionWithLevelKeyMaybe = foundOption ? [{ ...foundOption, levelKey }] : [];

    return [...branchPath, ...foundOptionWithLevelKeyMaybe];
  }, []);
};

// Category mode (D39): levels from 2 on may hold several ids ("x,y" in the URL, an array in the form)
const toFormValues = (pickedValues, name) =>
  Object.entries(pickedValues).reduce((acc, [levelKey, value]) => {
    const isSublevel = levelKey !== `${name}1`;
    return { ...acc, [levelKey]: isSublevel ? `${value}`.split(',') : value };
  }, {});

// What is selected in the category tree: the single path, and the last level when it holds several ids
const getCategorySelection = (primaryOptions, formValues, name) => {
  const path = [];
  let options = primaryOptions;
  for (let level = 1; options?.length > 0; level++) {
    const value = formValues[`${name}${level}`];
    const ids = Array.isArray(value) ? value : value ? [value] : [];
    const found = options.filter(o => ids.includes(o.option));
    if (found.length === 0) {
      break;
    }
    if (found.length > 1) {
      return { path, multiple: found };
    }
    path.push(found[0]);
    options = found[0].suboptions;
  }
  return { path, multiple: [] };
};

/**
 * SelectSingleFilter component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that extends the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} props.id - The id
 * @param {string} props.name - The name
 * @param {React.ReactNode} props.label - The label
 * @param {Array<string>} props.queryParamNames - The query param names
 * @param {Object} props.initialValues - The initial values
 * @param {Array<Object>} props.options - The options
 * @param {Function} props.onSubmit - The function to handle the submit
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {number} [props.contentPlacementOffset] - The content placement offset
 * @param {boolean} [props.isNestedEnum] - Whether the enum is nested
 * @returns {JSX.Element}
 */
const SelectSingleFilter = props => {
  const {
    rootClassName,
    className,
    showAsPopup = false,
    options,
    isNestedEnum = false,
    id,
    name,
    label,
    queryParamNames,
    initialValues,
    contentPlacementOffset = 0,
    onSubmit,
    getAriaLabel,
    ...rest
  } = props;

  const intl = useIntl();
  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // A nested enum is the category filter: level-1 radios, subcategory checkboxes (D39)
  const isCategoryTree = isNestedEnum;

  const classes = classNames(rootClassName || css.root, className);
  const labelClass = hasInitialValues ? css.labelPlainSelected : css.labelPlain;
  const labelForPlain = <span className={labelClass}>{label}</span>;

  // Pass the initial values with the name key so that
  // they can be passed to the correct field
  const pickedValues = pickInitialValuesForFieldSelectTree(name, initialValues, isNestedEnum);
  const pickedInitialValues = {
    [name]: isCategoryTree ? toFormValues(pickedValues, name) : pickedValues,
  };

  // Screen reader selection («L1/L2», or «L1: x, y» for several subcategories) and the opener label:
  // «Категория» → the selected name → «<parent>: N» for several subcategories (D39)
  const categoryTreeSelection = isCategoryTree
    ? getCategorySelection(options, pickedInitialValues[name], name)
    : null;
  const pathLabels = isCategoryTree
    ? categoryTreeSelection.path.map(option => option.label)
    : getBranchPath(options, pickedInitialValues[name]).map(option => option.label);
  const multipleLabels = isCategoryTree ? categoryTreeSelection.multiple.map(o => o.label) : [];
  const categorySelection =
    multipleLabels.length > 0
      ? `${pathLabels.join('/')}: ${multipleLabels.join(', ')}`
      : pathLabels.join('/');
  const labelForPopup = !isCategoryTree
    ? label
    : multipleLabels.length > 0
    ? intl.formatMessage(
        { id: 'SelectSingleFilter.labelMultiple' },
        { parent: pathLabels[pathLabels.length - 1], count: multipleLabels.length }
      )
    : pathLabels.length > 0
    ? pathLabels[pathLabels.length - 1]
    : label;

  const handleSubmit = queryParamNames => (values, options) => {
    const isArray = Array.isArray(queryParamNames);
    const hasMultipleQueryParams = isArray && queryParamNames.length > 1;
    const hasSingleQueryParam = isArray && queryParamNames.length === 1;
    const firstQueryParamName = isArray ? queryParamNames[0] : queryParamNames;

    // Nested options create multiple query params
    //   - E.g. categoryLevel1: 'v1', categoryLevel2: 'v2', categoryLevel3: 'v3'
    // If there's only a single query param in use, we flatten the value
    //   - E.g. categoryLevel: 'value'
    // Several subcategories (an array, in Console order) become a comma-separated value: "x,y"
    const usedValue = hasMultipleQueryParams
      ? queryParamNames.reduce((acc, p, i) => {
          const k = `${name}${i + 1}`;
          const raw = values?.[name]?.[k];
          const v = Array.isArray(raw) ? (raw.length > 0 ? raw.join(',') : null) : raw || null;
          return { ...acc, [p]: v };
        }, {})
      : hasSingleQueryParam
      ? { [firstQueryParamName]: values?.[name]?.[`${name}1`] || null }
      : values;
    onSubmit(usedValue, options);
  };

  const tree = isCategoryTree ? (
    <FieldSelectTree
      name={name}
      options={options}
      multiSelectSublevels
      id={`${id}.categories`}
      legend={label}
    />
  ) : (
    <FieldSelectTree name={name} options={options} />
  );

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      label={labelForPopup}
      ariaLabel={getAriaLabel(label, categorySelection)}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      keepLabelWhileOpen={isCategoryTree}
      popupClassName={isCategoryTree ? css.categoryPopupSize : undefined}
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit(queryParamNames)}
      initialValues={pickedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
      liveEdit
    >
      {tree}
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={labelForPlain}
      ariaLabel={getAriaLabel(label, categorySelection)}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit(queryParamNames)}
      initialValues={pickedInitialValues}
      {...rest}
    >
      {tree}
    </FilterPlain>
  );
};

export default SelectSingleFilter;
